import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Download, Search, Phone, Mail, Home, Calendar, Filter } from 'lucide-react';
import { API, getToken } from './shared';

const EnquiriesTab = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const debounce = setTimeout(() => fetchEnquiries(), 300);
    return () => clearTimeout(debounce);
  }, [searchText, dateFrom, dateTo]);

  const fetchEnquiries = async () => {
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/admin/enquiries?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setEnquiries(res.data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/admin/enquiries/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `proptick_enquiries_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setDateFrom('');
    setDateTo('');
  };

  const hasFilters = searchText || dateFrom || dateTo;

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso?.slice(0, 16) || '';
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl sm:text-3xl font-semibold" data-testid="enquiries-title">Customer Enquiries</h2>
        <button
          onClick={handleExport}
          disabled={exporting || enquiries.length === 0}
          className="h-10 px-5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-all flex items-center gap-2 disabled:opacity-50"
          data-testid="export-enquiries-excel"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, phone, email or property..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
              data-testid="enquiry-search"
            />
          </div>
          <button
            onClick={() => setShowDateFilters(!showDateFilters)}
            className={`h-10 px-4 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-all ${showDateFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid="enquiry-toggle-date"
          >
            <Filter className="w-4 h-4" /> Date
          </button>
          {hasFilters && (
            <button onClick={clearFilters} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-all" data-testid="enquiry-clear-filters">Clear</button>
          )}
        </div>
        {showDateFilters && (
          <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="enquiry-date-from" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="enquiry-date-to" />
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-4">{enquiries.length} enquiries{hasFilters ? ' (filtered)' : ''}</p>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : enquiries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Enquiries Yet</h3>
          <p className="text-slate-500">Customer enquiries will appear here when users contact sellers via WhatsApp.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200" data-testid="enquiries-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3.5 font-semibold text-slate-700 text-sm">Date</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-slate-700 text-sm">Customer</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-slate-700 text-sm">Property</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-slate-700 text-sm">Seller</th>
                  <th className="text-center px-5 py-3.5 font-semibold text-slate-700 text-sm">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enquiries.map((enq) => (
                  <tr key={enq.id} className="hover:bg-slate-50 transition-colors" data-testid={`enquiry-${enq.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="whitespace-nowrap">{formatDate(enq.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900 text-sm">{enq.customer_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Phone className="w-3 h-3" /> {enq.customer_phone}
                        </span>
                        {enq.customer_email && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="w-3 h-3" /> {enq.customer_email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900 text-sm truncate max-w-[220px]">{enq.listing_title}</p>
                      <span className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                        <Home className="w-3 h-3" /> {enq.listing_locality}, {enq.listing_city}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-slate-700">{enq.seller_name}</p>
                      <span className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                        <Phone className="w-3 h-3" /> {enq.seller_phone}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${enq.source === 'email' ? 'bg-violet-50 text-violet-700' : 'bg-green-50 text-green-700'}`}>
                        {enq.source === 'email' ? <Mail className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                        {enq.source === 'email' ? 'Email' : 'WhatsApp'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiriesTab;
